import { useEffect, useState, useCallback } from "react";
import { adminApi, API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FolderOpen, Upload, Download, X } from "lucide-react";

const CATEGORIES = ["Contract", "Participation Agreement", "Heter Izka", "Broker", "Bank Statement", "Tax Return", "Other"];
const MAX = 25 * 1024 * 1024;
const kb = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const field = "rounded-none border border-black bg-background h-10 px-3 focus:outline-none";

export const DocVault = ({ dealId, title = "Documents" }) => {
  const [docs, setDocs] = useState([]);
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const q = dealId ? `?deal_id=${dealId}` : "?scope=template";
      const { data } = await adminApi.get(`/site/admin/docs${q}`);
      setDocs(data.documents || []);
    } catch { /* silent */ }
  }, [dealId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > MAX) return toast.error("File exceeds 25MB");
    setBusy(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(String(r.result).split(",")[1] || "");
        r.onerror = rej; r.readAsDataURL(file);
      });
      const uploadId = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
      const CH = 3 * 1024 * 1024;
      for (let i = 0, seq = 0; i < b64.length; i += CH, seq++) {
        await adminApi.post("/site/admin/docs/chunk", { upload_id: uploadId, seq, data: b64.slice(i, i + CH) });
      }
      await adminApi.post("/site/admin/docs/finalize", {
        upload_id: uploadId, filename: file.name, content_type: file.type || "application/octet-stream",
        category, label, deal_id: dealId || null,
      });
      toast.success("Document uploaded");
      setLabel(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try { await adminApi.delete(`/site/admin/docs/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const dlUrl = (id) => `${API}/site/admin/docs/${id}/download?token=${encodeURIComponent(localStorage.getItem("admin_token") || "")}`;

  return (
    <div data-testid={`doc-vault-${dealId || "templates"}`} className="border-t border-black p-4 space-y-4">
      <div className="flex items-center gap-2">
        <FolderOpen size={15} />
        <h4 className="mono text-xs uppercase tracking-[0.25em]">{title}</h4>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground block mb-1">Category</label>
          <select data-testid="doc-category" value={category} onChange={(e) => setCategory(e.target.value)} className={`${field} min-w-[180px] mono text-[11px]`}>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground block mb-1">Label (optional)</label>
          <input data-testid="doc-label" value={label} onChange={(e) => setLabel(e.target.value)} className={`${field} w-full text-sm`} placeholder="e.g. 2025 Return" />
        </div>
        <label data-testid="doc-upload-btn" className={`inline-flex items-center gap-2 rounded-none border border-black h-10 px-5 mono text-[10px] uppercase tracking-[0.2em] cursor-pointer hover:bg-black hover:text-white transition-colors ${busy ? "opacity-50 pointer-events-none" : ""}`}>
          <Upload size={13} /> {busy ? "Uploading…" : "Upload"}
          <input type="file" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
        </label>
      </div>

      <div className="space-y-2">
        {docs.length === 0 && <p className="mono text-xs text-muted-foreground">No documents yet.</p>}
        {docs.map((d) => (
          <div key={d._id} data-testid={`doc-row-${d._id}`} className="border border-border px-3 py-2 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="font-medium text-sm truncate">{d.label || d.original_filename}</div>
              <div className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground truncate">{d.category} · {d.original_filename} · {kb(d.size)}</div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <a data-testid={`doc-dl-${d._id}`} href={dlUrl(d._id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mono text-[10px] uppercase tracking-[0.15em] border border-black px-2.5 py-1 hover:bg-black hover:text-white transition-colors"><Download size={11} /> Open</a>
              <button data-testid={`doc-del-${d._id}`} onClick={() => remove(d._id)} className="text-muted-foreground hover:text-destructive"><X size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
